import { actionCopyEnUs } from "./actionCopy";

export const analyticsCopyEnUs = {
    navTitle: "Analytics",
    navDescription: "Analyze the data from your patients",
    events: "Events",
    outcomes: "Outcomes",
    rawData: "Raw data",
    searchPatientsPlaceholder: "Search patients...",
    searchPatientsAria: "Search patients by name or other fields",
    clearPatientSearchAria: "Clear patient search",
    editWithAiPlaceholder: "Edit with AI",
    editWithAiAria: "Edit with AI",
    stopAiEditAria: "Stop AI",
    downloadCsv: "Download CSV",
    preparingCsv: "Preparing CSV...",
    downloadSas: "Export for SAS",
    preparingSas: "Preparing SAS...",
    sasExportHelp: "Download the selected study for a date range. Columns use variable OIDs and coded answers.",
    sasStudy: "Study",
    sasChooseStudy: "Choose a study",
    sasFrom: "From",
    sasTo: "To",
    sasExport: "Export",
    sasDateOrder: "The start date must be on or before the end date.",
    sasFailed: "The SAS export failed. Try again.",
    sasForbidden: "You do not have permission to export this study.",
    sasUnmapped: "This study has no variables mapped for SAS yet.",
    sasEmpty: "No entries in this study and date range.",
    sasRetry: "Try again",
    columns: actionCopyEnUs.columns,
    columnVisibilityDiffers: "Column visibility differs from default",
    filter: actionCopyEnUs.filter,
    addFilter: "Add filter",
    filtersFor: (name: string) => `Filters for "${name}"`,
    activeFilters: (count: number) => `${count} active filter${count === 1 ? "" : "s"}`,
    edit: actionCopyEnUs.edit,
    done: actionCopyEnUs.done,
    shared: "Shared",
    noData: "No data",
    noScoresForFilter: "No scores for this filter combination",
    failedToLoadEvents: "Failed to load events",
    noEvents: "No events",
    patients: "Patients",
    gender: "Gender",
    age: "Age",
    area: "Area",
    diaryId: "Diary id",
    patient: "Patient",
    date: "Date",
    optionsFor: (name: string) => `Options for ${name}`,
    shiftLeft: "Shift left",
    pieChart: "Pie Chart",
    donutChart: "Donut Chart",
    barChart: "Bar Chart",
    lineChart: "Line Chart",
    sparkCharts: "Spark Charts",
    statsTable: "Stats Table",
    statsTableSpark: "Stats Table + Spark Chart",
    table: "Table",
    stats: "Stats",
    timepointTable: "Timepoint Table",
    histogram: "Histogram",
    changeFromBaseline: "Change from Baseline",
    cumulativeEnrolment: "Cumulative Enrollment",
    engagementRate: "Completion among those who started",
    followUpCompletion: "Follow-up completion",
    engagementHint:
        "Share of started diaries that finished each follow-up. Baseline is the first questionnaire, so it should sit near the diary start rate. A late finish still counts as finished. Diaries that were never started are left out, and open windows are excluded.",
    engagementPercentCompleted: "Percent completed",
    engagementRateHint:
        "Of patients who started the questionnaire: completed within a month, answered only some questions within a month, or did not finish within a month. Patients who never started are not included.",
    engagementComplete: "On time",
    engagementPartial: "Partial within 1 month",
    engagementNotStarted: "Not started",
    engagementNotWithin: "Not within 1 month",
    engagementDue: "Due",
    engagementStarted: "Started",
    engagementOfPatientsDue: "Percent of patients due",
    engagementOfPatientsStarted: "Percent of patients who started",
    engagementOfDue: (completed: number, due: number) => `${completed} of ${due} due`,
    engagementLate: "Late",
    engagementMissed: "Missed",
    engagementCompletion: "Completion",
    engagementAllPatients: "All patients",
    engagementPatientsDue: (n: number) => `${n} due`,
    engagementHeatmapSplitHint: "Choose Split by in chart settings to compare sites, labels or clinicians.",
    engagementDotSize: "Dot size shows how many patients were due.",
    engagementDispositionHint:
        "Each patient counts once per clinical window. Open follow-ups are excluded until completed or their allowed window closes.",
    engagementTimingHint:
        "Days between submission and the scheduled time. The dot is the median difference and the bar is the middle 50%.",
    engagementTimingRangeHint:
        "Zero is the scheduled time. A positive number is days late and a negative number is days early. On time means within the allowed window either side of that time: the gap to the next follow-up, from 1 day up to 21 days.",
    engagementOnTimeCount: (onTime: number, completed: number) => `${onTime} of ${completed} on time`,
    engagementTimingWindowColumn: "Allowed window",
    engagementDayWindow: (min: number, max: number) =>
        min === max ? `${min}-day window` : `${min} to ${max} day window`,
    engagementTimingWindowSummary: (min: number, max: number) =>
        min === max ? `Within a ${min}-day window` : `Within a ${min} to ${max} day window`,
    engagementWithinWindowCount: (onTime: number, completed: number, min: number, max: number) =>
        min === max
            ? `${onTime} of ${completed} within a ${min}-day window`
            : `${onTime} of ${completed} within a ${min} to ${max} day window`,
    engagementOutsideDayWindow: (n: number, min: number, max: number) =>
        min === max ? `${n} outside the ${min}-day window` : `${n} outside the ${min} to ${max} day window`,
    engagementOutsideWindow: (n: number, window?: string) =>
        window ? `${n} outside the window (${window})` : `${n} outside the window`,
    engagementWindowDays: (min: number, max: number) =>
        min === max ? `±${min} ${min === 1 ? "day" : "days"}` : `±${min} to ${max} days`,
    engagementOutsideWindowColumn: "Outside the window",
    engagementGapDays: (days: number) => {
        const rounded = Math.round(days * 10) / 10;
        const unit = Math.abs(rounded) === 1 ? "day" : "days";
        if (rounded === 0) return `0 ${unit}`;
        return `${rounded > 0 ? "+" : ""}${rounded} ${unit}`;
    },
    engagementTimingAxisDays: (days: number) => {
        if (days === 0) return "0";
        const unit = Math.abs(days) === 1 ? "day" : "days";
        return `${days > 0 ? "+" : ""}${days} ${unit}`;
    },
    engagementTimingEmpty: "No completed scheduled follow-ups have timing data",
    engagementEarlier: "Earlier",
    engagementScheduled: "Scheduled",
    engagementLater: "Later",
    engagementMedian: "Median",
    engagementCompleted: "Completed",
    engagementMiddle50: "Middle 50%",
    engagementCompletedCount: (n: number) => `${n} completed`,
    engagementMetrics: "Engagement metrics",
    engagementMetricsHint:
        "Entry and diary completion use patient questionnaires on diaries that have started. The diary completion count is out of those started diaries. Clinician forms are left out.",
    engagementMetric: "Metric",
    engagementRateColumn: "Rate",
    engagementCount: "Count",
    engagementOnTimeRate: "On-time rate",
    engagementDiaryStartRate: "Diary start rate",
    engagementEntryCompletionRate: "Entry completion rate",
    engagementDiaryCompletionRate: "Diary completion rate",
    engagementDiariesStarted: (completed: number, due: number) => `${completed} of ${due} diaries started`,
    engagementEntriesCompleted: (completed: number, due: number) => `${completed} of ${due} entries completed`,
    engagementDiariesCompleted: (completed: number, due: number) => `${completed} of ${due} diaries complete`,
    engagementWindows: {
        baseline: "Baseline",
        w6: "Up to 6 weeks",
        m3: "6 weeks to 3 months",
        m6: "3 to 6 months",
        m12: "6 to 12 months",
        m12plus: "Over 12 months",
    } as Record<string, string>,
    engagementAllCompleted: "All completed",
    engagementSomeCompleted: "Some completed",
    engagementNoneStarted: "No follow-ups have been started yet",
    engagementOnTime: "On time",
    engagementUnderMonth: "Less than 1 month late",
    engagementOverMonth: "More than 1 month late",
    engagementEmpty: "No scheduled follow-ups are due yet",
    engagementPatient: "Patient",
    engagementClinician: "Clinician",
    engagementCohort: "Cohort",
    engagementByLabel: "Diary label",
    engagementByClinician: "Clinician",
    engagementByTracker: "Tracker",
    engagementFollowUp: "Follow-up",
    engagementFollowUpGrain: "Timing",
    engagementFollowUpAbsolute: "Absolute",
    engagementFollowUpGrouped: "Grouped",
    engagementFollowUpDaily: "Daily",
    engagementFollowUpWeekly: "Weekly",
    engagementFollowUpMonthly: "Monthly",
    engagementFollowUpQuarterly: "Quarterly",
    engagementFollowUpYearly: "Yearly",
    engagementFollowUpDays: "Days",
    engagementFollowUpWeeks: "Weeks",
    engagementFollowUpMonths: "Months",
    engagementFollowUpYears: "Years",
    engagementFollowUpAbsoluteHint: "Each follow-up stays on its own point, in schedule order.",
    engagementFollowUpGroupedHint: "Grouped shows Month 0, Month 1, Month 2. Month 0 is the first 30 days.",
    engagementFollowUpStep: (grain: "day" | "week" | "month" | "quarter" | "year", index: number) => {
        const label =
            grain === "day"
                ? "Day"
                : grain === "week"
                  ? "Week"
                  : grain === "month"
                    ? "Month"
                    : grain === "quarter"
                      ? "Quarter"
                      : "Year";
        return `${label} ${index}`;
    },
    engagementFollowUpGrainHint: (grain: "day" | "week" | "month" | "quarter" | "year") => {
        if (grain === "day") return "Daily shows Day 0, Day 1, Day 2. Day 0 is the procedure day.";
        if (grain === "week") return "Weekly shows Week 0, Week 1, Week 2. Week 0 is the first 7 days.";
        if (grain === "quarter")
            return "Quarterly shows Quarter 0, Quarter 1, Quarter 2. Quarter 0 is the first 90 days.";
        if (grain === "year") return "Yearly shows Year 0, Year 1, Year 2. Year 0 is the first year.";
        return "Monthly shows Month 0, Month 1, Month 2. Month 0 is the first 30 days.";
    },
    engagementGroupAria: "Engagement breakdown",
    engagementOf: (completed: number, due: number) => `${completed} of ${due}`,
    unlabelled: "Unlabeled",
    unassigned: "Unassigned",
    timepoint: "Timepoint",
    ageBracket: "Age Bracket",
    label: "Diary label",
    sex: "Sex",
    anatomicArea: "Anatomic Area",
    event: "Event",
    form: "Form",
    averageAge: "Average Age",
    patientCountsByAgeBracket: "Patient Counts by Age Bracket",
    ageDistribution: "Age Distribution",
    chartOfGender: (chart: string) => `${chart} of Gender`,
    patientCountsByGender: "Patient Counts by Gender",
    totalPatients: "Total Patients",
    meanField: (field: string) => `Mean ${field}`,
    formStats: (form: string) => `${form} Stats`,
    tableOf: (dataset: string) => `Table of ${dataset}`,
    sparkChartsOf: (form: string) => `Spark Charts of ${form}`,
    statsTableOf: (form: string) => `${form} Stats Table`,
    statsTableByTimepoint: (form: string) => `${form} Stats Table by Timepoint`,
    statsTableSparkOf: (form: string) => `${form} Stats Table + Spark Chart`,
    statsTableSparkByTimepoint: (form: string) => `${form} Stats Table + Spark Chart by Timepoint`,
    lineChartOverTime: (form: string) => `Line Chart of ${form} over Time`,
    byTimepoint: (form: string) => `${form} by Timepoint`,
    histogramOf: (subject: string) => `Histogram of ${subject}`,
    previousTreatments: (form: string) => `Previous Treatments · ${form}`,
    patientCountsBy: (group: string) => `Patient Counts by ${group}`,
    barChartOfBy: (subject: string, group: string) => `Bar Chart of ${subject} by ${group}`,
    chartOf: (chart: string, subject: string) => `${chart} of ${subject}`,
    chartOfBy: (chart: string, dataset: string, field: string) => `${chart} of ${dataset} by ${field}`,
    bySuffix: (title: string, split: string) => `${title} by ${split}`,
    changeFromBaselineSubject: (subject: string) => `Change from Baseline · ${subject}`,
    lsmeanChange: (subject: string) => `LSMean Change · ${subject}`,
    percentOfMax: (subject: string) => `% of Max · ${subject}`,
    percentChangeFromBaseline: (subject: string) => `% Change from Baseline · ${subject}`,
    score: "Score",
    shiftRight: "Shift right",
    rename: actionCopyEnUs.rename,
    duplicate: actionCopyEnUs.duplicate,
    share: actionCopyEnUs.share,
    delete: actionCopyEnUs.delete,
    cancel: actionCopyEnUs.cancel,
    addView: "Add view",
    addViewDescription: "Choose a charts dashboard or a raw data spreadsheet first.",
    addViewStepOf: (step: number, total: number) => `Step ${step} of ${total}`,
    addViewStepTypeTitle: "What kind of view?",
    addViewStepTypeDescription:
        "Choose a charts dashboard or a raw data spreadsheet. You can copy settings and ask AI next.",
    addViewStepSetupTitle: "Copy settings or ask AI",
    addViewStepSetupDescription: "Optional. Copy filters from another tab, describe what you want, or both.",
    addViewBack: "Back",
    addViewAiLabel: "Ask AI",
    addViewAiPlaceholderCharts: "Optional: describe the charts you want, e.g. WOMAC over time by age",
    addViewAiPlaceholderData: "Optional: describe filters, e.g. knee diaries for patients over 60",
    copySettingsFrom: "Copy settings from",
    none: actionCopyEnUs.none,
    charts: "Charts",
    data: "Data",
    chartsDescription: "Widgets, trends and summary stats",
    dataDescription: "Spreadsheet of patient rows and scores",
    createChartsView: "Create charts view",
    createDataView: "Create data view",
    refreshAria: "Refresh analytics data",
    addWidget: "Add widget",
    addWidgetFor: (name: string) => `Pick a form, optional field, and how to display it for "${name}".`,
    noWidgetsOnTab: "No widgets on this tab yet.",
    useAddWidgetToChart: (label: string) => `Use "Add widget" above to chart real ${label} data.`,
    diaryFallback: "diary",
    failedToLoadAnalytics: "Failed to load analytics data",
    failedToLoadData: "Failed to load data",
    failedToLoadPatientData: "Failed to load patient data. Refresh to try again.",
    loadingPatients: "Loading patients...",
    loadingAnalytics: (label: string) => `Loading ${label} data...`,
    analyticsFallback: "analytics",
    fieldFallback: "Field",
    noWidgetsYet: "No widgets yet. Add one from the palette or ask the AI assistant.",
    noWidgetsConfigured: "No widgets configured for this view.",
    clearFilters: "Clear filters",
    deleteViewTitle: "Delete view?",
    deleteViewDescription: (name: string) =>
        `This will permanently remove "${name}" and its widgets. This cannot be undone.`,
    showingRangeOf: (start: string, end: string, total: string, noun: string) =>
        `Showing ${start}-${end} of ${total} ${noun}`,
    rowSingular: "row",
    rowPlural: "rows",
    diarySingular: "diary",
    diaryPlural: "diaries",
    noPatientsMatch: (query: string) => `No patients match "${query}"`,
    noPatientsMatchFilters: "No patients match the current filters",
    searchColumnsPlaceholder: "Search columns...",
    searchColumnsAria: "Search columns",
    visibleColumns: "Visible columns",
    showAll: "Show all",
    hideAll: "Hide all",
    eventsCount: "events",
    acrossDiaries: (count: number) => `Across ${count} ${count === 1 ? "diary" : "diaries"}`,
    eventPrefix: (label: string) => `Event · ${label}`,
    chartSettings: "Chart settings",
    severalDataTabsHint:
        "You already have several data tabs - consider adjusting filters or columns on an existing tab.",
    chartN: (n: number) => `Chart ${n}`,
    dataN: (n: number) => `Data ${n}`,
    showingColumnsOf: (visible: string, total: string) => `Showing ${visible} of ${total} columns`,
    noColumnsMatch: (query: string) => `No columns match "${query}"`,
    filterCategories: {
        mine: "My role",
        demographic: "Demographics",
        label: "Diary label",
        org: "Organization",
        form: "Form",
        event: "Event",
        status: "Diary status",
    },
    filterAnnotations: {
        mine: "Diaries where you have any of these roles.",
        label: "Diaries with any of these labels.",
        org: "Diaries in any of these organizations.",
        status: "Diaries in any of these statuses.",
    },
    filteredBy: "Filtered by",
    sexValues: { Female: "Female", Male: "Male", Other: "Unknown" },
    rawCellExplanations: {
        genderInferred:
            "Gender is inferred from the title in the patient name (e.g. Mr, Mrs). Edit the Name column to change it.",
        genderPrivate:
            "Gender is a cohort demographic inferred from title (e.g. Mr, Mrs). Name stays private for your access level.",
        patientNamePrivate: "Patient name is private for your access level.",
        patientNameHidden: "Patient name is hidden without identifiable access.",
        dobPrivate: "Date of birth is private for your access level.",
        ageEditable:
            "Age at the diary start date, from date of birth - not the patient's age today. Edit Date of birth to change it.",
        agePrivate:
            "Age at the diary start date, from date of birth - not the patient's age today. Date of birth stays private for your access level.",
        ageReadonly: "Age at the diary start date, from date of birth - not the patient's age today.",
        sideDerived: "Which side the questionnaires on this row refer to. Choose both if both sides are affected.",
        noQuestionnaireSide: "No questionnaire on this row to set a side for.",
        noEditableEvent: "No editable event on this diary.",
        unsupportedEventValue: "This value is not supported by the event's edit form.",
        productArea: "Area is determined by the selected product. Edit the Product cell to change it.",
        noEventDate: "No event date to edit on this diary.",
        noEditableArea: "No editable event supplies this area.",
        shockwaveProcedure: "Shockwave is the intervention type, not an editable detail.",
        noEditableProcedure: "No editable event supplies this procedure.",
        ageDerived: "Age at the diary start date, calculated from date of birth. Not the patient's current age.",
        durationDerived: "Calculated from the diary start date to today.",
        ageAtEventDerived: (event: string) => `Calculated from date of birth and the earliest ${event} date.`,
        noTracker: "No tracker at this timepoint for this diary.",
        noSideTracker: (side: string) => `No ${side} tracker at this timepoint for this diary.`,
        notOnSchedule: "This timepoint is not on this tracker's schedule.",
        notOnSideSchedule: (side: string) => `This timepoint is not on the ${side} tracker schedule.`,
        multipleEventRows: (event: string) =>
            `This ${event} has multiple detail rows. Edit it from the patient diary to avoid replacing the other details.`,
        multipleEvents: (event: string) =>
            `Multiple ${event} events contribute to this cell. Edit them from the patient diary.`,
        multipleDates: (event: string) =>
            `Multiple ${event} dates contribute to this cell. Edit them from the patient diary.`,
        editDobHelper: "Age at diary start is calculated from this date of birth.",
        editEventDateHelper: (event: string) => `Edit the ${event} date.`,
    },
    eventDatasetLabels: {
        injection: "Injections",
        surgery: "Surgeries",
        shockwave: "Shockwaves",
        product: "Products",
        education: "Consultations",
        medication: "Medications",
    } as Record<string, string>,
    rawSideValues: { left: "left", right: "right", both: "both", middle: "middle" } as Record<string, string>,
    showDetailsAria: "Show details",
    widgetTypes: {
        stat: "Stats card",
        bar: "Bar",
        histogram: "Histogram",
        donut: "Donut",
        pie: "Pie",
        line: "Line/area",
        spark: "Spark charts",
        statsTable: "Stats table",
        statsTableSpark: "Stats table + spark chart",
        diaryStatus: "Diary status",
        timepoints: "Timepoint table",
        table: "Table",
        enrolment: "Cumulative enrollment",
        engagement: "Follow-up completion",
        engagementTable: "Follow-up table",
        engagementRate: "Among those who started",
        engagementDisposition: "Patient disposition",
        engagementTiming: "Timing against schedule",
        engagementRetention: "Retention curve",
        engagementKpi: "Headline numbers",
        engagementHeatmap: "Heatmap by split",
        engagementDots: "Dot plot",
    },
    valueTransforms: {
        absolute: "Absolute",
        changeFromBaseline: "Change from baseline",
        lsmeanChange: "LSMean Change",
        percentOfMax: "% of max",
        percentChangeFromBaseline: "% change from baseline",
    },
    summaryTable: {
        unknownForm: "Unknown form",
        noEntries: "No entries",
        metric: "Metric",
        distribution: "Distribution",
        boxPlot: "Box plot",
        mean: "Mean",
        median: "Median",
        mode: "Mode",
        sd: "SD",
        ci: "95% CI",
        frequency: "Frequency",
        value: "Value",
        count: "Count",
        percent: "%",
        indicator: "Indicator",
        proportion: "Proportion",
        entries: "Entries",
        yes: "Yes",
        no: "No",
    },
    /** Raw data table: static column group + header labels. Unlisted keys fall back to source label. */
    rawColumnGroups: {
        Patient: "Patient",
        INJECTION: "INJECTION",
        SURGERY: "SURGERY",
        SHOCKWAVE: "SHOCKWAVE",
        PRODUCT: "PRODUCT",
        CONSULTATION: "CONSULTATION",
    } as Record<string, string>,
    /** Event-column headers, keyed by their English catalog label. */
    rawFieldLabels: {
        Date: "Date",
        Substance: "Substance",
        Area: "Area",
        Site: "Site",
        Side: "Side",
        Dose: "Dose",
        Type: "Type",
        Procedure: "Procedure",
        Product: "Product",
        Topic: "Topic",
        "Provided by": "Provided by",
        "Delivered by": "Delivered by",
        "Referring clinician": "Referring clinician",
        "Injecting clinician": "Injecting clinician",
        "Operating clinician": "Operating clinician",
        "Shockwaving clinician": "Shockwaving clinician",
    } as Record<string, string>,
    rawColumnLabels: {
        diary_id: "Diary id",
        patient_name: "Name",
        date_of_birth: "Date of birth",
        age: "Age",
        gender: "Gender",
        organisation: "Organization",
        side: "Side",
        area: "Area",
        procedure: "Procedure",
    } as Record<string, string>,
    /** Raw data table: score cell status popovers/tooltips. */
    scoreUnavailable: "Unavailable",
    scoreAdd: "Add score",
    scoreNotDueYet: "Not due yet",
    scoreLate: "Late",
    scoreClickToAddTooltip: "Click the cell to add a score",
    scoreLateTooltip: "This timepoint is late. Click the cell to add a score.",
    scoreNotDueTooltip: "This timepoint is not due yet. Click the cell to add a score.",
    scoreNoTrackerTooltip: "No tracker at this timepoint for this diary.",
    editScore: "Edit score",
    decreaseScoreAria: "Decrease score",
    increaseScoreAria: "Increase score",
    scoreRange: (min: number, max: number) => `Range ${min} - ${max}`,
    scoreMin: (min: number) => `Min ${min}`,
    scoreMax: (max: number) => `Max ${max}`,
    confidential: "Confidential",
    confidentialAria: "Confidential patient details. Show why.",
    /** Timing status sentences shared with entryTimingStatus.ts. */
    thisTimepoint: "This timepoint",
    dueDate: (date: string) => `Due ${date}`,
    enteredDate: (date: string) => `Entered ${date}`,
    entryNotEnteredYet: "Not entered yet",
    timepointHasRecordedScore: (point: string) => `${point} has a recorded score.`,
    timepointDates: (point: string, dates: string) => `${point}: ${dates}.`,
    timepointNoTracker: (point: string) => `${point} has no tracker on this diary, so a score cannot be added.`,
    clickToAddScore: "Click to add a score.",
    diaryStatus: "Diary status",
    diaryStatusTable: {
        searchPlaceholder: "Search by patient name",
        searchAria: "Search patients",
        clearSearch: "Clear search",
        columns: "Columns",
        columnsDescription:
            "Choose which columns to show. Use the switch on a group to show or hide every column in it. Name and date of birth stay hidden until you turn them on.",
        sparkline: "Sparkline",
        patientDetails: "Patient details",
        reset: "Reset",
        save: "Save",
        questionnaire: "Questionnaire",
        patient: "Patient",
        patientName: "Patient name",
        dateOfBirth: "Date of birth",
        score: "Score",
        status: "Status",
        startDate: "Start date",
        organisation: "Organization",
        labels: "Labels",
        age: "Age",
        sex: "Sex",
        area: "Area",
        procedure: "Procedure",
        noPatients: "No patients match these filters.",
        loading: "Loading patients...",
        unknownPatient: "Unknown patient",
        showing: (from: string, to: string, total: string): string => `Showing ${from}-${to} of ${total}`,
    },
};

export function localizeDashboardViewName(name: string, copy: typeof analyticsCopyEnUs): string {
    if (name === "Events") return copy.events;
    if (name === "Outcomes") return copy.outcomes;
    if (name === "Raw data") return copy.rawData;
    if (name === "Diary status") return copy.diaryStatus;
    return name;
}
